import React, { cloneElement, forwardRef, isValidElement, useEffect, useRef, } from 'react';
import { Stack, styled, InputBase as MuiInputBase, Select as MuiSelect, } from '@mui/material';
import clsx from 'clsx';
import { selectClasses, getStyle, inputClasses, getInputStyle } from './style';
import Option from './Option';
import { IconChevronSmall, IconClose } from '../../internal/icons';
import { useMixedValue } from '../../hooks/useMixedValue';
import { Button } from '../../Button';
import { findAccessor } from '../../Shared/dom';
import { extractAriaProps } from '../../Shared/props';
import { useTranslation } from '../../hooks/useTranslation';
const InputBase = forwardRef((props, ref) => {
    const { size, ...rest } = props;
    return (React.createElement(MuiInputBase, { classes: {
            root: inputClasses.root,
            focused: inputClasses.focused,
            error: inputClasses.error,
            disabled: inputClasses.disabled,
        }, ...rest, ref: ref }));
});
const StyledInput = styled(InputBase, {
    shouldForwardProp: (prop) => prop !== 'size',
})((props) => {
    const { theme, size } = props;
    return getInputStyle(theme, { size });
});
const SelectIconComponent = React.forwardRef((props, ref) => React.createElement(IconChevronSmall, { ...props, ref: ref }));
const SelectBase = (props, ref) => {
    const { t } = useTranslation('Select');
    const { size = 'medium', options, children, status, required, disabled, readOnly, SelectDisplayProps, placeholder, listMaxHeight, onKeyDownCapture, MenuProps: { sx: menuSx, ...restMenuProps } = {}, noDataText = t('noData'), noOptionsText, allowClear, value, defaultValue, onChange, longOptionsDisplay: _longOptionsDisplay = 'grow', ...restProps } = props;
    // We do not handle the long options if autoWidth is true
    const longOptionsDisplay = restProps.autoWidth ? 'grow' : _longOptionsDisplay;
    const [ariaProps, rest] = extractAriaProps(restProps);
    const [innerValue, setInnerValue] = useMixedValue(value, defaultValue);
    // Enhanced effect to handle form resets properly
    const prevValueRef = useRef(value);
    useEffect(() => {
        // This detects form reset by checking if the value changed from something to undefined
        if (value === undefined && prevValueRef.current !== undefined) {
            // Form reset detected - we should use defaultValue if available,
            // otherwise clear the innerValue to show the placeholder
            setInnerValue(defaultValue !== undefined ? defaultValue : '');
        }
        // Track previous value to detect transitions
        prevValueRef.current = value;
    }, [value, defaultValue, setInnerValue]);
    const SelectIconWithClearComponent = React.useMemo(() => 
    // We have to define the component inside render function because we cannot modify the props pass to it via MUI.
    // We must keep the `onChange` in the closure.
    // eslint-disable-next-line react/no-unstable-nested-components
    React.forwardRef((iconProps, ref) => {
        function handleClick(event) {
            const newValue = '';
            if (onChange) {
                // Redefine target to allow value to be read.
                // This allows seamless integration with the most popular form libraries.
                // https://github.com/mui/material-ui/issues/13485#issuecomment-676048492
                // Clone the event to not override `target` of the original event.
                const { nativeEvent } = event;
                const clonedEvent = new nativeEvent.constructor(nativeEvent.type, nativeEvent);
                Object.defineProperty(clonedEvent, 'target', {
                    writable: true,
                    value: { value: newValue },
                });
                onChange(clonedEvent, null);
            }
            setInnerValue(newValue);
            const root = findAccessor(event.currentTarget, (ele) => ele.classList.contains(inputClasses.root));
            root?.querySelector(`.${selectClasses.select}`)?.focus();
        }
        return (React.createElement(Stack, { className: selectClasses.clearGroup },
            React.createElement(Button, { variant: "icon", type: "tertiary", size: "small", "aria-label": t('clear'), onClick: handleClick, className: selectClasses.clearButton },
                React.createElement(IconClose, { "aria-hidden": true })),
            React.createElement(IconChevronSmall, { ...iconProps, ref: ref })));
    }), [t, onChange, setInnerValue]);
    function handleChange(event, child) {
        setInnerValue(event.target.value);
        onChange?.(event, child);
    }
    const menuProps = {
        transitionDuration: 0,
        classes: {
            paper: selectClasses.paper,
            list: selectClasses.list,
        },
        anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
        },
        transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
        },
        sx: (theme) => ({
            ...getStyle(theme, {
                size,
                placeholder,
                listMaxHeight,
                longOptionsDisplay,
            }),
            ...menuSx,
        }),
        ...restMenuProps,
    };
    function handleKeyDown(e) {
        // Treat space key as enter key to trigger select action
        if (e.key === ' ' &&
            !e.shiftKey &&
            !e.altKey &&
            !e.ctrlKey &&
            !findAccessor(e.target, (ele) => ele.classList.contains(selectClasses.clearButton))) {
            const event = new KeyboardEvent('keydown', {
                bubbles: true,
                code: 'Enter',
                key: 'Enter',
                keyCode: 13,
            });
            e.target.dispatchEvent(event);
            e.stopPropagation();
            e.preventDefault();
        }
        onKeyDownCapture?.(e);
    }
    const clearable = allowClear &&
        !disabled &&
        !readOnly &&
        !(!innerValue || (Array.isArray(innerValue) && !innerValue.length));
    const childComponents = children
        ? React.Children.toArray(children)
        : (options ?? []).map((o) => (React.createElement(Option, { key: o.value, value: o.value, disabled: o.disabled, size: size }, o.label)));
    return (React.createElement(MuiSelect, { MenuProps: menuProps, input: React.createElement(StyledInput, { error: status === 'error', size: size }), classes: {
            root: selectClasses.root,
            select: clsx(selectClasses.select, {
                [selectClasses.clearable]: clearable,
            }),
            icon: selectClasses.icon,
            disabled: selectClasses.disabled,
        }, IconComponent: clearable ? SelectIconWithClearComponent : SelectIconComponent, SelectDisplayProps: {
            role: 'combobox',
            'aria-required': required,
            // We do not add the placeholder to the aria cue, since the placeholder is currently placed into the ::before style of content.
            // Adding it to aria-placeholder will make the screen reader read it twice.
            // We may consider replace the implement since it relies much on MUI.
            // 'aria-placeholder': placeholder,
            ...ariaProps,
            ...SelectDisplayProps,
        }, 
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        disabled: disabled || readOnly, required: required, onKeyDownCapture: handleKeyDown, ...rest, 
        // Handle form resets properly by making sure empty values are treated as empty strings
        value: innerValue === undefined || innerValue === null || innerValue === '' ? '' : innerValue, onChange: handleChange, ref: ref },
        !childComponents.length ? (React.createElement("div", { className: selectClasses.noData }, noOptionsText ?? noDataText)) : null,
        childComponents.map((child) => {
            if (isValidElement(child) && child.type === Option) {
                return cloneElement(child, {
                    multiline: longOptionsDisplay === 'multiline',
                });
            }
            return child;
        })));
};
const InternalSelect = styled(forwardRef(SelectBase))((props) => {
    const { theme, size = 'medium', placeholder } = props;
    return getStyle(theme, { size, placeholder });
});
const Select = InternalSelect;
Select.Option = Option;
export default Select;
